import {
  ChangeEvent, useMemo, useRef, useState, useEffect,
} from 'react';
import {
  Drawer,
  Tree,
  Input,
  Heading,
  Select,
  Button,
  Text,
  ITreeProps,
  Grid,
  Actions,
  Divider,
  Spacer,
  Menu,
  OptionDropdown,
  type TChangeEvent,
  Flex,
} from '@oliasoft-open-source/react-ui-library';
import { FeatureFlags } from '@oliasoft/feature-flags';
import { getTokenPayload } from '@oliasoft/pkce';
import { FileUpload } from 'client/components/file-upload/file-upload';
import { HierarchyLevelType, IArchive } from 'common/interfaces/hierarchy-entities';
import { ability, ActionMap } from '@oliasoft/authorization-ui';
import translations from 'client/internationalization/translation-map.json';
import { useTranslation } from 'react-i18next';
import { caslEnums } from 'common/enums/casl-enums';
import { ConnectedProps, connect, useDispatch } from 'react-redux';
import { AccessSubjectTypes } from 'common/interfaces/access-rights';
import {
  addToOpenedItems,
  removeFromOpenedItems,
  resetOpenedItems,
  setSelectedArchiveId,
} from 'client/store/ui/hierarchy-tree/hierarchy-tree';
import { getEntityAccessRightsList } from 'client/store/entities/access-rights/access-rights';
import { setAccessRightsBatch } from 'client/components/access-control-modal/access-utils';
import { RootState } from 'client/store/configureStore';
import { TFunction } from 'i18next';
import ClientConfig from 'client/config';
import { WellStatusType } from 'common/enums/entity-enums';
import { AccessControlModal, IAccessControlModalsRef } from '@oliasoft/hierarchy-gui';
import { IHierarchyTreeItem } from './hierarchy.interfaces';

import { HierarchyItemStates } from './hierarchy-enums';
import {
  getChildHierarchyEntityType, hierarchyTypeAddLabel, groupHierarchyTreeBy, hierarchyLevelTypeName, getParentHierarchyEntityType, filterHierarchy,
} from './hierarchy.utils';
import './hierarchy-drawer.css';

const getTranslatedGroupingOptions = (t: TFunction) => [
  {
    label: t(translations.hierarchy_groupByCountry),
    value: HierarchyLevelType.Country,
  },
  {
    label: t(translations.hierarchy_groupByField),
    value: HierarchyLevelType.Field,
  },
  {
    label: t(translations.hierarchy_groupBySite),
    value: HierarchyLevelType.Site,
  },
  {
    label: t(translations.hierarchy_groupByWell),
    value: HierarchyLevelType.Well,
  },
  {
    label: t(translations.hierarchy_groupByWellbore),
    value: HierarchyLevelType.Wellbore,
  },
  {
    label: t(translations.hierarchy_groupByDesign),
    value: HierarchyLevelType.Design,
  },
];

const TRANSLATED_FILTER_PLACEHOLDER: { [key: string]: string; } = {
  [HierarchyLevelType.Country]: translations.hierarchy_filterByCountry,
  [HierarchyLevelType.Field]: translations.hierarchy_filterByField,
  [HierarchyLevelType.Site]: translations.hierarchy_filterBySite,
  [HierarchyLevelType.Well]: translations.hierarchy_filterByWell,
  [HierarchyLevelType.Wellbore]: translations.hierarchy_filterByWellbore,
  [HierarchyLevelType.Design]: translations.hierarchy_filterByDesign,
};

type HierarchyDrawerProps = PropsFromRedux & {
  hierarchy: { [id: string]: IHierarchyTreeItem };
  topLevel: HierarchyLevelType;
  archives: IArchive[];
  setTopLevel: (topLevel: HierarchyLevelType) => void;
  loadMore: (parentItem: Pick<IHierarchyTreeItem, 'id' | 'entityType' | 'nodeId'>) => void;
  getIsLoading: (parentItem: IHierarchyTreeItem | null) => boolean;
  createItem: (entityType: HierarchyLevelType, parentItem: IHierarchyTreeItem | null) => void;
  editItem: (item: IHierarchyTreeItem, type: HierarchyItemStates) => void;
  onItemDelete: (item: IHierarchyTreeItem, type: HierarchyItemStates) => void;
  getTotalChildCount: (loadType: HierarchyLevelType, parentId: string) => number | undefined;
};

const HierarchyDrawer = ({
  hierarchy,
  topLevel,
  archives,
  selectedArchiveId,
  users,
  entityAccessRightsList,
  entityAccessRightsMetadata,
  setSelectedArchiveId,
  setTopLevel,
  loadMore,
  getIsLoading,
  createItem,
  editItem,
  onItemDelete,
  getTotalChildCount,
  openedItems,
  addToOpenedItems,
  removeFromOpenedItems,
  resetOpenedItems,
  activeItem,
}: HierarchyDrawerProps) => {
  const dispatch = useDispatch();
  const childRef = useRef<IAccessControlModalsRef>(null);
  const { t } = useTranslation();

  const defaultDesignTypeLabel = t(translations.hierarchy_designType);
  const allDesignTypesLabel = t(translations.hierarchy_allDesignTypes);
  const [isOpen, setOpen] = useState(true);
  const [filterText, setFilterText] = useState('');
  const [uploadModalVisible, setUploadModalVisible] = useState(false);
  const [uploadNodeId, setUploadNodeId] = useState<string | undefined>();
  const [designTypeLabel, setDesignTypeLabel] = useState<string>(defaultDesignTypeLabel);
  const [filterType, setFilterType] = useState<{ entityType: HierarchyLevelType, key: keyof IHierarchyTreeItem, values: string[] } | undefined>(undefined);
  const [designTypeOptions, setDesignTypeOptions] = useState<any>([
    { label: WellStatusType.Actual, value: WellStatusType.Actual },
    { label: WellStatusType.Planned, value: WellStatusType.Planned },
    { label: WellStatusType.Prototype, value: WellStatusType.Prototype }
  ]);

  const onChangeDesignType = (event: TChangeEvent<HTMLInputElement>) => {
    const values: { label: string, value: string, selected?: boolean }[] = event.target.value;
    const selectedDesignTypes = values.filter(item => item.selected);

    if (selectedDesignTypes?.length === values.length) {
      setDesignTypeLabel(allDesignTypesLabel);
    } else {
      const label = selectedDesignTypes.map(item => item.label).join(', ');
      setDesignTypeLabel(label || defaultDesignTypeLabel);
    }
    setDesignTypeOptions(values);
    setFilterType({ entityType: HierarchyLevelType.Design, key: 'well_status', values: selectedDesignTypes.map(({value}) => value)});
  };

  const tokenPayload = getTokenPayload(
    ClientConfig.config.pkce.provider,
    ClientConfig.config.applicationId,
    ClientConfig.config.idpServerUrl,
  );

  const onFilterInputTextChange = (event: React.ChangeEvent<{ value: string }>) => {
    setFilterText(event.target.value);
  };

  const makeAccessControlOption = (
    item: IHierarchyTreeItem,
    subjectType = AccessSubjectTypes.User,
    icon: 'key' | 'lock' = 'key',
  ) => ({
    icon,
    type: 'Option',
    label: t(translations.authorization_accessControl),
    onClick: () => {
      childRef.current?.accessRightsConfigurationStarted({
        item,
        userList: users,
        currentUserSubjectId: tokenPayload!.subjectId || '',
        subjectType,
      });
      dispatch(getEntityAccessRightsList(item.entityType, item.id, subjectType));
    },
  });

  useEffect(() => {
    childRef.current?.accessRightListUpdated({ accessRightsList: entityAccessRightsList, metadata: entityAccessRightsMetadata });
  }, [entityAccessRightsList]);

  const itemsWithActions = useMemo(() => {
    return Object.values(hierarchy as unknown as IHierarchyTreeItem[]).map((item) => {
      const childLevel = getChildHierarchyEntityType(item.entityType);

      const addHierarchyItemOption = (childLevel && childLevel !== HierarchyLevelType.Object) ? { // Uploading File objects is handled in other way
        type: 'Option',
        label: hierarchyTypeAddLabel[childLevel],
        icon: 'add',
        onClick: () => {
          createItem(childLevel, item);
        },
      } : null;

      const uploadFileAction = item.entityType === HierarchyLevelType.Node ? {
        type: 'Option',
        label: t(translations.importExport_uploadFile),
        onClick: () => {
          setUploadModalVisible(true);
          setUploadNodeId(item.id);
        },
      } : null;

      const canMangeAccess = ability.can(ActionMap.MANAGE, caslEnums.manageAccessRights);
      const accessManagementAction = (canMangeAccess) ? makeAccessControlOption(item) : null;

      const dotsMenu = [
        ...(addHierarchyItemOption ? [addHierarchyItemOption] : []),
        {
          type: 'Option', icon: 'edit', label: t(translations.edit), onClick: () => editItem(item, HierarchyItemStates.Edit),
        },
        {
          type: 'Option', icon: 'delete', label: t(translations.delete), onClick: () => onItemDelete(item, HierarchyItemStates.Delete),
        },
        ...(uploadFileAction ? [uploadFileAction] : []),
        ...(canMangeAccess ? [accessManagementAction] : []),
      ];
      const actions: any[] = [
        {
          label: 'Menu',
          subActions: dotsMenu,
        },
      ];
      if (item.rights) {
        actions.splice(0, 0, makeAccessControlOption(item, AccessSubjectTypes.ApiKey, 'lock'));
      }
      return {
        ...item,
        active: item.id === activeItem?.id,
        actions,
      };
    });
  }, [activeItem, hierarchy]);

  const itemHasChild: ITreeProps['itemHasChild'] = ({ data }) => {
    return data.entityType && data.entityType !== HierarchyLevelType.Design; // 'Design' is bottom level shown in hierarchy drawer. Children is shown in main view
  };

  const onGroupByChange = (event: ChangeEvent<{ value: HierarchyLevelType }> | any) => {
    if (topLevel !== event.target.value) {
      resetOpenedItems();
    }
    setTopLevel(event.target.value);
  };

  const collapseTree = () => {
    resetOpenedItems();
  };

  const onItemToggle = (node: any, isOpen: boolean) => {
    const item = {
      ...node,
      ...node.data,
    };
    if (isOpen) {
      addToOpenedItems(node.id);
    } else {
      removeFromOpenedItems(node.id);
    }
    if (isOpen) {
      loadMore(item);
    }
  };

  const groupedHierarchy = groupHierarchyTreeBy(itemsWithActions, topLevel);
  const treeItems = filterHierarchy(groupedHierarchy, filterText, topLevel, filterType);

  const getChildCount = (parentId: string | number) => groupedHierarchy.reduce((acc, item) => (item.parent === parentId ? acc + 1 : acc), 0);

  const openedTreeItems = [] as string[];
  const extraItems = [];
  Object.keys(openedItems)
    .forEach(id => {
      openedTreeItems.push(id);
      const item = hierarchy[id];
      if (!item) return;
      const totalChildCount = getTotalChildCount(getChildHierarchyEntityType(item.entityType) || HierarchyLevelType.Object, id) || 0;

      if (getChildCount(id) !== totalChildCount) {
        extraItems.push({
          id: `${id}_extra`,
          parent: id,
          name: getIsLoading(item) ? (
            <Text muted>{t(translations.hierarchy_loading)}</Text>
          ) : (
            <Button
              label={t(translations.hierarchy_loadMore)}
              onClick={() => loadMore(item)}
              small
            />
          ),
        } as unknown as IHierarchyTreeItem);
      }
    });
  const parentId = topLevel === HierarchyLevelType.Country && selectedArchiveId ? selectedArchiveId : ''; // parentid only matters when "group by country" is selected
  if (getChildCount(0) < (getTotalChildCount(topLevel, parentId) || 0)) {
    extraItems.push({ // one more item to load more for the very top level
      id: 'top_level_extra',
      parent: 0,
      name: getIsLoading(null) ? (
        <Text muted>{t(translations.hierarchy_loading)}</Text>
      ) : (
        <Button
          label={t(translations.hierarchy_loadMore)}
          onClick={() => (topLevel === HierarchyLevelType.Country ? loadMore({
            id: parentId, entityType: HierarchyLevelType.Archive,
          }) : loadMore({ entityType: getParentHierarchyEntityType(topLevel)!, id: parentId }))}
          small
        />
      ),
    } as unknown as IHierarchyTreeItem);
  }

  const treeItemsWithSubheaders = treeItems.concat(extraItems).map((item) => {
    const createdBy = item?.createdBy;
    const { applicationId } = ClientConfig.config;

    return {
      ...item,
      name: item.entityType === topLevel && item.entityType !== HierarchyLevelType.Design ? item.name : (
        <div>
          <span style={{ marginRight: 'var(--padding-xs)' }}>{item.name}</span>
          <Text faint>{item.entityType === HierarchyLevelType.Design ? <span style={{ color: item.well_status === WellStatusType.Actual ? 'var(--color-info-300)' : '' }}>{item.well_status}</span> : hierarchyLevelTypeName[item.entityType]}</Text>
          {createdBy && createdBy.toLowerCase() !== applicationId.toLowerCase() && (
            <div className="createdByText">
              <Text muted>
                {t(translations.hierarchy_createdBy, { value: createdBy.charAt(0).toUpperCase() + createdBy.slice(1) })}
              </Text>
            </div>
          )}
        </div>
      ),
    };
  });

  const list = {
    items: treeItemsWithSubheaders,
  };

  const onChangeArchive = (event: any) => {
    if (selectedArchiveId === event.target.value) return;
    setSelectedArchiveId(event.target.value);
  };

  const selectedArchive = selectedArchiveId ? hierarchy[selectedArchiveId] : null;
  const archiveItem: IHierarchyTreeItem = {
    ...selectedArchive,
    name: selectedArchive?.name || '',
    id: selectedArchiveId || '',
    key: selectedArchiveId || '',
    entityType: HierarchyLevelType.Archive,
    parent: -1,
  };

  const archiveMenu = archiveItem ? [
    {
      type: 'Option', icon: 'edit', label: t(translations.edit), onClick: () => editItem(archiveItem, HierarchyItemStates.Edit),
    },
    {
      // This will automatically trigger (in the DB) deleting of the nodes and their children
      // related to the corresponding hierarchy items by `nodeid`!
      type: 'Option', icon: 'delete', label: t(translations.delete), onClick: () => onItemDelete(archiveItem, HierarchyItemStates.Delete),
    },
    ...(ability.can(ActionMap.MANAGE, caslEnums.manageAccessRights) ? [
      makeAccessControlOption(archiveItem, AccessSubjectTypes.User),
    ] : []),
    ...(FeatureFlags.isActive('Multiple Archives') ? [
      { type: 'Divider' },
      {
        type: 'Option',
        icon: 'add',
        label: hierarchyTypeAddLabel[HierarchyLevelType.Archive],
        onClick: () => {
          createItem(HierarchyLevelType.Archive, null);
        },
      },
    ] : []),
    ...(topLevel === HierarchyLevelType.Country ? [{ type: 'Divider' },
    {
      type: 'Option',
      icon: 'add',
      label: hierarchyTypeAddLabel[HierarchyLevelType.Country],
      onClick: () => selectedArchiveId && createItem(HierarchyLevelType.Country, archiveItem),
    }] : []),
  ] : [];

  const actionsCollapse = [{
    icon: 'collapse all',
    onClick: collapseTree,
    disabled: openedTreeItems.length === 0,
  }] as any[];

  return (
    <Drawer open={isOpen} setOpen={setOpen} button width={400} closedWidth={50} border background="var(--color-neutral-50)">
      <div className={`drawerContent ${isOpen ? 'visible' : 'hidden'}`}>
        <div
          style={{
            padding: 'var(--padding)',
          }}
        >
          <Heading marginBottom={0} top>{t(translations.explore)}</Heading>
          <Spacer height="var(--padding-xs)" />
          <Grid gap="var(--padding-sm)">
            <Grid columns="1fr auto" gap="var(--padding-xs)">
              <Select
                options={(archives as IArchive[]).map((archiveOption) => ({
                  label: archiveOption.name,
                  value: archiveOption.archiveid,
                }))}
                value={selectedArchiveId}
                onChange={onChangeArchive}
              />
              <Flex alignItems="center">
                <Menu menu={{
                  sections: archiveMenu,
                  trigger: 'Component',
                  component: <Button
                    basic
                    colored="muted"
                    round
                    icon="menu"
                    small
                  />,
                }}
                />
              </Flex>
            </Grid>
            <Grid columns={ topLevel === HierarchyLevelType.Design ? '2fr 2fr 1fr auto' : '1fr 1fr auto' } gap="var(--padding-xs)">
              <Select
                small
                value={topLevel}
                onChange={onGroupByChange}
                options={getTranslatedGroupingOptions(t)}
              />
              { topLevel === HierarchyLevelType.Design &&
                <OptionDropdown
                  small
                  label={designTypeLabel}
                  onChange={onChangeDesignType}
                  options={designTypeOptions}
                />
              }
              <Input
                small
                type="search"
                placeholder={t(TRANSLATED_FILTER_PLACEHOLDER[topLevel])}
                onChange={onFilterInputTextChange}
                value={filterText}
              />
              <Actions actions={actionsCollapse} />
            </Grid>
          </Grid>
        </div>
        <Divider margin={0} />
        <div>
          <Tree
            list={list as any}
            itemHasChild={itemHasChild}
            onItemToggle={onItemToggle}
            isInitialOpen={openedTreeItems}
            autoOpenActiveItems
          />
        </div>
      </div>
      {uploadNodeId && uploadModalVisible && <FileUpload isModal visible={uploadModalVisible} onClose={() => setUploadModalVisible(false)} initialNodeId={uploadNodeId} archiveid={selectedArchiveId!} />}
      <AccessControlModal
        ref={childRef}
        handleEntityAccessRightsUpdated={setAccessRightsBatch}
      />
    </Drawer>
  );
};

const mapStateToProps = ({ ui, entities }: RootState) => ({
  openedItems: ui.hierarchyTree.openedItems,
  selectedArchiveId: ui.hierarchyTree.selectedArchiveId,
  activeItem: ui.hierarchyTree.activeItem,
  users: entities?.user.users,
  entityAccessRightsList: entities?.entityAccessRights.accessRights,
  entityAccessRightsMetadata: entities?.entityAccessRights.metadata,
});

const mapDispatchToProps = {
  addToOpenedItems,
  removeFromOpenedItems,
  resetOpenedItems,
  setSelectedArchiveId,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

const Container = connector(HierarchyDrawer);

export { Container as HierarchyDrawer };
